import { CommonUIError } from '@afterpay/types'
import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { ChangeEvent, forwardRef, KeyboardEvent, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useThemeClass } from '../Theme'
import { noop } from '../utils/function'
import { KeyCodes } from '../utils/keyboard'
import { getFirstNonEmptyString } from '../utils/string'
import styles from './style.module.scss'

declare global {
  interface Window {
    OTPCredential: any
  }
}

const STRING_NUMBER_ONLY_REGEX = /^\d+$/

interface CodeEntryProps extends CommonUIError {
  codeEntryLength?: number
  masked?: boolean
  onComplete?: (response: string) => any
  testNameSpace?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  autoComplete?: string
  disabled?: boolean
  name?: string
}

export const CodeEntry = forwardRef<any, CodeEntryProps>(
  (
    { error = false, errorText, codeEntryLength = 4, masked = false, onComplete = noop, disabled = false, testNameSpace = '', name = '', ...otherProps },
    ref
  ) => {
    const [value, setValue] = useState('')
    const [focusIndex, setFocusIndex] = useState<number | undefined>()
    const themeClass = useThemeClass()
    const isPegasusTheme = themeClass === 'themePegasus'
    const isKelpieTheme = themeClass === 'themeKelpie'
    const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme

    const hasTestId = testNameSpace.length > 0
    const fauxBoxRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const errorId = `${getFirstNonEmptyString(name, otherProps['aria-label'], testNameSpace)}-code-entry-error`

    const moveCursor = (index: number): void => {
      const inputElement = inputRef?.current
      if (inputElement != null) {
        inputElement.selectionStart = index
        inputElement.selectionEnd = index + 1
      }
    }

    const handleValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const newValue = e.target.value
      const lengthDiff = newValue.length - value.length
      const replacedCharacter = lengthDiff === 0

      if (newValue === '' || STRING_NUMBER_ONLY_REGEX.test(newValue)) {
        setValue(newValue)

        if (typeof focusIndex !== 'undefined') {
          // already focus on the input field
          if (newValue.length === 0) {
            setFocusIndex(0)
          } else {
            setFocusIndex(replacedCharacter ? focusIndex + 1 : focusIndex + lengthDiff)
          }
        } else {
          // entering focus state
          setFocusIndex(newValue.length)
        }

        if (newValue.length >= codeEntryLength) {
          onComplete(newValue)
        }
      }

      if (focusIndex === undefined) {
        setFocusIndex(0)
      }
    }

    const handleInputBlur = (): void => {
      setFocusIndex(undefined)
    }

    const handleInputKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
      const { key } = e
      if (key === KeyCodes.ArrowLeft) {
        const newFocusIndex = focusIndex !== undefined && focusIndex > 0 ? focusIndex - 1 : 0
        setFocusIndex(newFocusIndex)
      }
      if (key === KeyCodes.ArrowRight) {
        const allowMove = focusIndex !== undefined && value.length > focusIndex
        const newFocusIndex = focusIndex !== undefined && focusIndex < codeEntryLength - 1 ? focusIndex + 1 : codeEntryLength - 1
        if (allowMove) {
          setFocusIndex(newFocusIndex)
        }
      }
      if (key === KeyCodes.Home || key === KeyCodes.ArrowUp) {
        setFocusIndex(0)
      }

      if (key === KeyCodes.End || key === KeyCodes.ArrowDown) {
        const allNumbersAreFilled = value.length === codeEntryLength
        const newFocusIndex = allNumbersAreFilled ? value.length - 1 : value.length
        setFocusIndex(newFocusIndex)
      }
    }

    const handleFauxBoxClick = (fauxBoxIndex: number) => (): void => {
      const allowSelection = value.length >= fauxBoxIndex
      const newFocusIndex = allowSelection ? fauxBoxIndex : value.length

      inputRef?.current?.focus()
      fauxBoxRef.current?.scrollIntoView({ behavior: 'smooth' })
      setFocusIndex(newFocusIndex)
    }

    const renderFauxBox = (): JSX.Element => (
      <>
        {Array.from({ length: codeEntryLength }, (_, index) => {
          const isInteractive = value.length >= index && !disabled
          const fauxBoxStyle = clsx(
            styles.fauxBox,
            !disabled && focusIndex === index && styles.fauxBoxFocus,
            isInteractive && isPegasusOrKelpieTheme && styles.isInteractive
          )
          const textStyle = clsx(
            styles.text,
            masked && value[index] && styles.masked,
            !disabled && focusIndex === index && value[index] === undefined && styles.focusFauxBoxEmpty,
            !disabled && focusIndex === index && value[index] !== undefined && styles.focusFauxBoxFilled
          )
          return (
            <span
              className={fauxBoxStyle}
              key={`faux-box-${index}`}
              onClick={handleFauxBoxClick(index)}
              {...(hasTestId && { 'data-testid': `${testNameSpace}-code-entry-${index}` })}
            >
              <span className={styles.fauxSquare}>
                <span className={textStyle}>{masked && value[index] ? '•' : value[index]}</span>
              </span>
            </span>
          )
        })}
      </>
    )

    const componentClassName = clsx(styles.component, error && styles.modError, disabled && styles.modDisabled, styles[themeClass])

    useEffect(() => {
      if (typeof focusIndex !== 'undefined') {
        moveCursor(focusIndex)
      }
    }, [focusIndex])

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (value.length === 0) {
          setFocusIndex(value.length)
          inputRef?.current?.focus()
          fauxBoxRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
      },
      addEventListener: (event: string, callback: () => void) => {
        inputRef?.current?.addEventListener(event, callback)
      },
      removeEventListener: (event: string, callback: () => void) => {
        inputRef?.current?.removeEventListener(event, callback)
      },
    }))

    useEffect(() => {
      const webOTPAvailable = typeof window !== 'undefined' && navigator.credentials && 'OTPCredential' in window && 'AbortController' in window

      if (webOTPAvailable) {
        const ac = new AbortController()

        navigator.credentials
          .get({
            otp: {
              transport: ['sms'],
            },
            signal: ac.signal,
          })
          .then((otp) => {
            // @ts-expect-error - outdated PasswordCredential definitions
            setValue(otp?.code)
          })
          .catch(() => {
            ac.abort()
          })

        setTimeout(() => {
          ac.abort() // abort webOtp after 10mins
        }, 1 * 60 * 10000)

        return () => ac.abort() // abort webOtp when component unmount
      }
    }, [])

    return (
      <div className={componentClassName} {...(hasTestId && { 'data-testid': `${testNameSpace}-code-entry` })}>
        <input
          disabled={disabled}
          value={value}
          type='tel'
          ref={inputRef}
          inputMode='numeric'
          maxLength={codeEntryLength}
          className={styles.input}
          onBlur={handleInputBlur}
          onKeyUp={handleInputKeyUp}
          onChange={handleValueChange}
          autoComplete='one-time-code'
          {...(Boolean(error) && errorText && { 'aria-describedby': errorId })}
          {...(hasTestId && { 'data-testid': `${testNameSpace}-code-entry-input` })}
          {...otherProps}
        />
        <div className={styles.fauxBoxWrapper} ref={fauxBoxRef}>
          {renderFauxBox()}
        </div>
        {error && errorText && (
          <div id={errorId} className={styles.errorText} {...(hasTestId && { 'data-testid': `${testNameSpace}-code-entry-error` })}>
            <span aria-live='polite'>{isCallable(errorText) ? errorText() : errorText}</span>
          </div>
        )}
      </div>
    )
  }
)
